import React, { useState, useRef, useEffect, useCallback } from 'react';
import { slideApiService } from '../services/slideApiService';
import { Slide, CreateSlideWithImagesRequest, RateLimitError } from '../types';
import { useShop } from '../contexts/ShopContext';
import CategoryEditor from './CategoryEditor';
import LocationPlaceEditor from './LocationPlaceEditor';
import ImageCapture, { ImageCaptureHandle } from './ImageCapture';
import RateLimitModal from './RateLimitModal';

interface CreateSlideModalProps {
  shopId: string;
  onClose: () => void;
  onSlideCreated: (slide: Slide, autoAnalyze?: boolean) => void;
}

const MAX_IMAGES_PER_SLIDE = 10;

const CreateSlideModal: React.FC<CreateSlideModalProps> = ({
  shopId,
  onClose,
  onSlideCreated
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [categories, setCategories] = useState<string[]>([]);
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  const [placeId, setPlaceId] = useState<string | undefined>(undefined);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rateLimitError, setRateLimitError] = useState<RateLimitError | null>(null);
  const [autoAnalyze, setAutoAnalyze] = useState(() => {
    try {
      const stored = localStorage.getItem(`csai_auto_analyze_${shopId}`);
      return stored !== null ? stored === 'true' : true;
    } catch {
      return true;
    }
  });
  const [createAsActive, setCreateAsActive] = useState(() => {
    try {
      const stored = localStorage.getItem(`csai_create_as_active_${shopId}`);
      return stored !== null ? stored === 'true' : true;
    } catch {
      return true;
    }
  });
  const { canManageShop } = useShop();
  const [isMobileCameraActive, setIsMobileCameraActive] = useState(false);
  const imageCaptureRef = useRef<ImageCaptureHandle>(null);

  const handleCameraStateChange = useCallback((isActive: boolean, isMobile: boolean) => {
    setIsMobileCameraActive(isActive && isMobile);
  }, []);

  // Load last used Location/Place from localStorage on mount
  useEffect(() => {
    try {
      const storageKey = `csai_last_location_place_${shopId}`;
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const data = JSON.parse(stored);
        // Handle all three cases:
        // 1. No data: locationId and placeId both undefined
        // 2. Location only: locationId set, placeId undefined
        // 3. Location + Place: both set
        if (data.locationId) {
          setLocationId(data.locationId);
        }
        if (data.placeId) {
          setPlaceId(data.placeId);
        }
        console.log('Loaded last Location/Place from localStorage:', data);
      }
    } catch (error) {
      console.warn('Failed to load last Location/Place from localStorage:', error);
      // Gracefully degrade - feature continues without persistence
    }
  }, [shopId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate at least one image is provided
      if (selectedFiles.length === 0) {
        setError('At least one image is required');
        setLoading(false);
        return;
      }

      // Validate maximum images limit
      if (selectedFiles.length > MAX_IMAGES_PER_SLIDE) {
        setError(`Maximum of ${MAX_IMAGES_PER_SLIDE} images allowed per slide`);
        setLoading(false);
        return;
      }

      // Stop camera before submitting
      imageCaptureRef.current?.stopCamera();

      // First file is primary, rest are additional
      const primaryImage = selectedFiles[0];
      const additionalImages = selectedFiles.length > 1 ? selectedFiles.slice(1) : [];

      // Debug: Log Location/Place state before building slide object
      console.log('CreateSlideModal - handleSubmit - Current state:', {
        locationId,
        placeId,
        categories,
        name: formData.name,
        description: formData.description
      });

      // Build slide object with only provided fields
      const slide: any = {};
      if (formData.name.trim()) {
        slide.name = formData.name.trim();
      }
      if (formData.description.trim()) {
        slide.description = formData.description.trim();
      }
      if (categories.length > 0) {
        slide.categories = categories;
      }

      // Include Location/Place if selected
      if (locationId) {
        slide.locationId = locationId;
        console.log('Adding locationId to slide:', locationId);
      }
      if (placeId) {
        slide.placeId = placeId;
        console.log('Adding placeId to slide:', placeId);
      }

      const request: CreateSlideWithImagesRequest = {
        slide,
        primaryImage: primaryImage
      };

      if (additionalImages.length > 0) {
        request.additionalImages = additionalImages;
      }

      const newSlide = await slideApiService.createSlideWithImages(request, shopId, autoAnalyze, createAsActive);

      // Save Location/Place to localStorage for next time (if selected)
      if (locationId || placeId) {
        try {
          const storageKey = `csai_last_location_place_${shopId}`;
          const data = {
            locationId,
            placeId,
            timestamp: Date.now()
          };
          localStorage.setItem(storageKey, JSON.stringify(data));
          console.log('Saved Location/Place to localStorage for next slide:', data);
        } catch (storageError) {
          console.warn('Failed to save Location/Place to localStorage:', storageError);
          // Non-blocking - user continues unaffected
        }
      }

      // Save preferences for next time
      try {
        localStorage.setItem(`csai_auto_analyze_${shopId}`, String(autoAnalyze));
        localStorage.setItem(`csai_create_as_active_${shopId}`, String(createAsActive));
      } catch (storageError) {
        // Non-blocking
      }

      onSlideCreated(newSlide, autoAnalyze);
    } catch (err) {
      console.error('Failed to create slide:', err);
      if (err instanceof RateLimitError) {
        setRateLimitError(err);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to create slide. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Normal modal (hidden when mobile camera is active) */}
      {/* Uses visibility:hidden instead of display:none so that the position:fixed
          camera overlay inside ImageCapture remains visible on screen */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px',
          ...(isMobileCameraActive ? { visibility: 'hidden' as const } : {})
        }}
        onClick={onClose}
      >
        <div
          style={{
            backgroundColor: 'var(--bg-card)',
            borderRadius: '8px',
            width: '100%',
            maxWidth: '700px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}
          onClick={e => e.stopPropagation()}
        >
        {/* Header */}
        <div
          style={{
            padding: '24px',
            borderBottom: '1px solid var(--border-light)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between'
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: '24px',
              fontWeight: 600,
              color: 'var(--text-primary)'
            }}
          >
            Create New Slide
          </h2>
          <button
            onClick={onClose}
            type="button"
            style={{
              background: 'none',
              border: 'none',
              fontSize: '28px',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              padding: '0 8px',
              lineHeight: 1
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = 'var(--text-accent)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = 'var(--text-accent)';
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Scrollable Content */}
        <form onSubmit={handleSubmit} style={{ flex: 1, overflowY: 'auto', padding: '24px', display: 'flex', flexDirection: 'column' }}>
          {error && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: 'var(--error-bg)',
              border: '1px solid var(--error-border)',
              borderRadius: '6px',
              color: 'var(--error-text)',
              marginBottom: '24px',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          {/* Image Capture - REQUIRED (at top for fast workflow) */}
          <div style={{ marginBottom: '24px' }}>
            <h3
              style={{
                margin: 0,
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--text-primary)',
                marginBottom: '16px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              Images *
            </h3>

            <ImageCapture
              ref={imageCaptureRef}
              selectedFiles={selectedFiles}
              onFilesChange={setSelectedFiles}
              maxImages={MAX_IMAGES_PER_SLIDE}
              onError={setError}
              onCameraStateChange={handleCameraStateChange}
            />
          </div>

          {/* Basic Information - OPTIONAL (Fast Workflow Optimized) */}
          <div style={{ marginBottom: '24px' }}>
            {/* Location / Place - Most frequently used field */}
            <div style={{ marginBottom: '16px' }}>
              <LocationPlaceEditor
                locationId={locationId}
                placeId={placeId}
                onLocationChange={(newLocationId) => {
                  console.log('CreateSlideModal - onLocationChange called with:', newLocationId);
                  setLocationId(newLocationId);
                }}
                onPlaceChange={(newPlaceId) => {
                  console.log('CreateSlideModal - onPlaceChange called with:', newPlaceId);
                  setPlaceId(newPlaceId);
                }}
                shopId={shopId}
              />
            </div>

            {/* Categories - Second most frequently used field */}
            <div style={{ marginBottom: '16px' }}>
              <CategoryEditor
                categories={categories}
                onCategoriesChange={setCategories}
                shopId={shopId}
              />
            </div>
          </div>

          {/* Auto-Analyze Toggle */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={autoAnalyze}
                onChange={(e) => {
                  setAutoAnalyze(e.target.checked);
                  if (!e.target.checked) {
                    setCreateAsActive(false);
                  }
                }}
                style={{ marginRight: '8px' }}
              />
              <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)' }}>
                Run Quick Analyze on create
              </span>
            </label>
            <p style={{ margin: '4px 0 0 24px', fontSize: '12px', color: 'var(--text-secondary)' }}>
              Automatically analyzes and updates your new Slide
            </p>
          </div>

          {/* Create as Active Toggle — only visible when autoAnalyze is on and user has permission */}
          {autoAnalyze && canManageShop && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={createAsActive}
                  onChange={(e) => setCreateAsActive(e.target.checked)}
                  style={{ marginRight: '8px' }}
                />
                <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)' }}>
                  Create as active
                </span>
              </label>
              <p style={{ margin: '4px 0 0 24px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                Automatically activate your new Slide
              </p>
            </div>
          )}

          {/* Primary Actions - Positioned for fast workflow */}
          <div
            style={{
              paddingTop: '16px',
              paddingBottom: '16px',
              borderTop: '1px solid var(--border-light)',
              borderBottom: '1px solid var(--border-light)',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              marginBottom: '24px'
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '10px 20px',
                backgroundColor: 'transparent',
                border: '1px solid var(--border-medium)',
                color: 'var(--text-primary)',
                fontSize: '14px',
                fontWeight: 500,
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || selectedFiles.length === 0}
              style={{
                padding: '10px 20px',
                backgroundColor: loading || selectedFiles.length === 0 ? 'var(--disabled-bg)' : 'var(--primary)',
                border: 'none',
                color: loading || selectedFiles.length === 0 ? 'var(--disabled)' : 'var(--text-inverse)',
                fontSize: '14px',
                fontWeight: 600,
                borderRadius: '6px',
                cursor: loading || selectedFiles.length === 0 ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Creating...' : 'Create Slide'}
            </button>
          </div>

          {/* Additional Details - Less frequently used fields */}
          <div style={{ marginBottom: '24px' }}>
            <h3
              style={{
                margin: 0,
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--text-secondary)',
                marginBottom: '16px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              Additional Details (Optional)
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <label
                htmlFor="name"
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: 'var(--text-primary)',
                  marginBottom: '6px'
                }}
              >
                Name
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter slide name (optional)"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '14px',
                  border: '1px solid var(--border-light)',
                  borderRadius: '6px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label
                htmlFor="description"
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: 'var(--text-primary)',
                  marginBottom: '6px'
                }}
              >
                Description
              </label>
              <textarea
                id="description"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe your slide (optional)..."
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '14px',
                  border: '1px solid var(--border-light)',
                  borderRadius: '6px',
                  outline: 'none',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>
        </form>
      </div>
    </div>

    {/* Rate Limit Modal */}
    {rateLimitError && (
      <RateLimitModal
        error={rateLimitError}
        onClose={() => setRateLimitError(null)}
      />
    )}
    </>
  );
};

export default CreateSlideModal;